import { AnimatePresence, motion } from 'framer-motion';
import { CSSProperties, useRef } from 'react';
import { Box, Center, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
// NOTE(DS migration): CustomSpinner is a commonComponent with no DS-primitive equivalent yet — kept.
import CustomSpinner from '~/commonComponents/CustomSpinner';
import { ProfessionalHubRedesign } from '~/constants/ff.posthog';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useScrollRestore } from '~/hooks/useScrollRestore';
import { buildCardRating } from '../../buildCardRating';
import { CardActionsHeader } from '../CardActionsHeader';
import { EmptyFilteredList } from '../EmptyFilteredList';
import { HubCardList } from '../shared/HubCardList';
import { ProfessionalCard } from '../ProfessionalCard';
import { EmptyRecommendedPros } from './EmptyRecommendedPros';
import { useRecommendedProsList } from './hooks/useRecommendedProsList';
import { ProfessionalCardActions } from './ProfessionalCardActions';
import { ProfessionalCardActionsLegacy } from './ProfessionalCardActionsLegacy';
import { ProfessionalCardDetails } from './ProfessionalCardDetails';
import { ProfessionalCardDetailsLegacy } from './ProfessionalCardDetailsLegacy';

// ── inline styles (no DS prop equivalent) ──────────────────────────────────
/** position/textAlign have no DS prop on Center */
const introCenterStyle: CSSProperties = { position: 'relative', textAlign: 'center' };

export const RecommendedProsList = () => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useScrollRestore('RecommendedProsList', scrollRef);

  const isNewCardDetails = useFeatureFlag(ProfessionalHubRedesign);

  const { recommendedProfessionals, isLoading, isEmpty, isFiltered, filter } =
    useRecommendedProsList();

  if (isLoading) {
    return <CustomSpinner isLoading size={24} />;
  }

  if (isEmpty) {
    return (
      <>
        {isFiltered && <EmptyFilteredList />}
        {!isFiltered && <EmptyRecommendedPros />}
      </>
    );
  }

  if (!isNewCardDetails) {
    return (
      <Box width="100%">
        <Center style={introCenterStyle}>
          <Typography italic color={colors.neutral[500]}>
            The following professionals have shown reliability at other nearby offices. Send an
            invite and fill your shift faster!
          </Typography>
        </Center>

        <div
          ref={scrollRef}
          style={{
            maxWidth: '827px',
            marginTop: 32,
            display: 'flex',
            justifyContent: 'center',
            marginLeft: 'auto',
            marginRight: 'auto',
            height: 600,
            overflowY: 'auto',
          }}
        >
          <motion.div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              padding: '0 50px',
            }}
            layout
          >
            <CardActionsHeader actions={['Invite', 'Block']} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <AnimatePresence>
                {recommendedProfessionals?.map((item) => (
                  <ProfessionalCard
                    key={item?.id}
                    user={{
                      id: item?.id,
                      fullname: item?.fullname,
                      profileImage: item?.profile_image,
                      professions: item?.professions,
                      profession:
                        item?.professions?.find((prof) => prof === filter) ||
                        item?.professions?.[0],
                      isAvailable: item?.isAvailable ?? true,
                      rating: buildCardRating(
                        item.rating == null
                          ? undefined
                          : {
                              averageRating: item.rating,
                              averageProfessionalSkills: item.averageProfessionalSkills,
                              averageInterpersonalSkills: item.averageInterpersonalSkills,
                              averageTimeManagement: item.averageTimeManagement,
                            },
                      ),
                    }}
                    Details={ProfessionalCardDetailsLegacy}
                    Actions={ProfessionalCardActionsLegacy}
                    showRemoveIcon={false}
                  />
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </Box>
    );
  }

  return (
    <HubCardList
      headerActions={['Block', 'Invite']}
      scrollRef={scrollRef}
      intro="The following professionals have shown reliability at other nearby offices. Send an invite and fill your shift faster!"
    >
      {recommendedProfessionals?.map((item) => (
        <ProfessionalCard
          key={item?.id}
          user={{
            id: item?.id,
            fullname: item?.fullname,
            profileImage: item?.profile_image,
            professions: item?.professions,
            profession:
              item?.professions?.find((prof) => prof === filter) || item?.professions?.[0],
            isAvailable: item?.isAvailable ?? true,
            activityLevel: item?.activity_level,
            yearsOfExperience: item?.years_of_experience,
            completedJobs: item?.completed_jobs,
            badges: item?.badges ?? [],
            rating: buildCardRating(
              item.rating == null
                ? undefined
                : {
                    averageRating: item.rating,
                    averageProfessionalSkills: item.averageProfessionalSkills,
                    averageInterpersonalSkills: item.averageInterpersonalSkills,
                    averageTimeManagement: item.averageTimeManagement,
                  },
            ),
          }}
          Details={ProfessionalCardDetails}
          Actions={ProfessionalCardActions}
          showRemoveIcon={false}
          isNewDesign
        />
      ))}
    </HubCardList>
  );
};
